import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { GroupBalance } from 'repository/timekeeper/@types/GroupBalance';

type ResponseGroupBalance = GroupBalance;

const GroupBalancesTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<GroupBalance[]>([]);
    const [loading, setLoading] = useState(false);
    //const [selectedGroupBalance, setSelectedGroupBalance] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<GroupBalance[]>([]);
    const [selectedGroupBalanceIds, setSelectedGroupBalanceIds] = useState<GridInputRowSelectionModel>([]);
    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t("accruals.accrual_codes.table.columns.name"), flex: 1 },
        { field: 'description', headerName: t("general.description"), flex: 1 },
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.group_balance.getAll()
            .then((data: ResponseGroupBalance[]) => {
                setRows(data);
            })
            .catch((error) => {
                console.error('Error al obtener los group balances:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService.group_balance]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedGroupBalanceIds([]);
    }

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                getRowId={(row) => row.group_balance_id}
                rowSelectionModel={selectedGroupBalanceIds}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    const selectedIds = newRowSelectionModel as (string | number)[];
                    const row = rows.filter((r) => selectedIds.includes(r.group_balance_id));
                    setSelectedGroupBalanceIds(newRowSelectionModel);
                    setSelected(row);
                }}
                checkboxSelection
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) =>
                        count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                            : `${count.toLocaleString()} fila seleccionada`,
                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    }
                }}
            />
            <SubMenu
                selected={selected}
                cleanSelected={cleanSelected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
            />
        </div>
    );
}

export { GroupBalancesTable };